// src/views/settings/ServerSettings.tsx
import Card from "components/card";
import CustomButton from "components/button";
import { useQuery, useMutation } from "@tanstack/react-query";
import { reqFunction } from "utils/API";
import { renderToast } from "utils/globalUtils";
import { useEffect, useMemo, useState } from "react";
import PasswordInput from "components/fields/PasswordInput";
import { MdAdd, MdDelete, MdClose } from "react-icons/md";

type NumOrEmpty = number | "";
type LogsLimits = { count: NumOrEmpty; bytes: NumOrEmpty };
type ConsumptionLimits = { count: NumOrEmpty; bytes: NumOrEmpty };
type BackupServer = {
  ip: string;
  port: NumOrEmpty;
  password: string;
  user?: string;
  remote_dir?: string;
};
type EmailFrom = { email: string; password: string };
type EmailToItem = { name: string; email: string };
type MQTT = { ip: string; port: NumOrEmpty };

type Uploads = {
  max_bytes: NumOrEmpty;
  types: string[];
};

type ServerConfig = {
  logs_limits: LogsLimits;
  consumption_limits: ConsumptionLimits;
  backup_server: BackupServer;
  email_from: EmailFrom;
  email_to: EmailToItem[];
  mqtt: MQTT;
  uploads: Uploads;
};

const DEFAULT_CONFIG: ServerConfig = {
  logs_limits: { count: "", bytes: "" },
  consumption_limits: { count: "", bytes: "" },
  backup_server: { ip: "", port: "", password: "", user: "", remote_dir: "" },
  email_from: { email: "", password: "" },
  email_to: [{ name: "", email: "" }],
  mqtt: { ip: "", port: "" },
  uploads: { max_bytes: "", types: [] },
};

const unitMap = [
  { key: "B", factor: 1, label: "بایت" },
  { key: "KB", factor: 1024, label: "کیلوبایت" },
  { key: "MB", factor: 1024 ** 2, label: "مگابایت" },
  { key: "GB", factor: 1024 ** 3, label: "گیگابایت" },
] as const;
type UnitKey = (typeof unitMap)[number]["key"];

const isValidIP = (v: string) =>
  /^(\d{1,3}\.){3}\d{1,3}$/.test(v) &&
  v.split(".").every((oct) => {
    const n = Number(oct);
    return n >= 0 && n <= 255;
  });

const isValidEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
const positiveInt = (n: NumOrEmpty) =>
  n === "" || (Number.isInteger(n) && n >= 0);
const portValid = (p: NumOrEmpty) => p === "" || (p >= 1 && p <= 65535);

const isValidUser = (u?: string) => !u || /^[a-z_][a-z0-9._-]*$/i.test(u);

const isValidRemoteDir = (p?: string) => !p || /^\/[^\0]*$/.test(p);

// helper for simple type validation: ".pdf" or "image/png"
const isValidFileTypeToken = (t: string) => {
  const s = t.trim();
  if (!s) return false;
  if (s.startsWith(".")) return /^[.][a-z0-9]+([.-][a-z0-9]+)*$/i.test(s);
  // very loose MIME check: type/subtype
  if (s.includes("/")) return /^[a-z0-9.+-]+\/[a-z0-9.+-]+$/i.test(s);
  return false;
};

function splitBytes(bytes: NumOrEmpty): { value: NumOrEmpty; unit: UnitKey } {
  if (bytes === "" || bytes === undefined || bytes === null)
    return { value: "", unit: "MB" };
  const b = Number(bytes);
  for (let i = unitMap.length - 1; i >= 0; i--) {
    const u = unitMap[i];
    if (b % u.factor === 0 && b >= u.factor) {
      return { value: b / u.factor, unit: u.key };
    }
  }
  return { value: b, unit: "B" };
}
function mergeBytes(value: NumOrEmpty, unit: UnitKey): NumOrEmpty {
  if (value === "" || value === undefined || value === null) return "";
  const factor = unitMap.find((u) => u.key === unit)!.factor;
  return Number(value) * factor;
}

const Field = ({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: React.ReactNode;
}) => (
  <label className="flex flex-col gap-1">
    <span className="text-sm font-medium">{label}</span>
    {children}
    {hint && <span className="text-xs text-gray-500">{hint}</span>}
    {error && <span className="text-xs text-red-600">{error}</span>}
  </label>
);

const Section = ({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) => (
  <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
    <div className="mb-4">
      <h3 className="text-lg font-semibold">{title}</h3>
      {subtitle && <p className="mt-1 text-sm text-gray-500">{subtitle}</p>}
    </div>
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">{children}</div>
  </div>
);

const NumberInput = ({
  value,
  onChange,
  placeholder,
}: {
  value: NumOrEmpty;
  onChange: (n: NumOrEmpty) => void;
  placeholder?: string;
}) => (
  <input
    type="number"
    className="w-full rounded-xl border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-300"
    value={value}
    placeholder={placeholder}
    onChange={(e) => {
      const raw = e.target.value;
      onChange(raw === "" ? "" : Number(raw));
    }}
  />
);

export const ServerSettings = () => {
  // form state
  const [config, setConfig] = useState<ServerConfig>(DEFAULT_CONFIG);
  const [original, setOriginal] = useState<ServerConfig>(DEFAULT_CONFIG);
  const [logsUnit, setLogsUnit] = useState<UnitKey>("MB");
  const [logsVal, setLogsVal] = useState<NumOrEmpty>("");
  const [consUnit, setConsUnit] = useState<UnitKey>("MB");
  const [consVal, setConsVal] = useState<NumOrEmpty>("");

  // uploads size UI state
  const [uploadsUnit, setUploadsUnit] = useState<UnitKey>("MB");
  const [uploadsVal, setUploadsVal] = useState<NumOrEmpty>("");

  // input buffer for adding types
  const [typeInput, setTypeInput] = useState<string>("");

  // GET
  const { data, isLoading, status, refetch } = useQuery({
    queryFn: () => reqFunction("Config/admin/getServerSetups", {}),
    queryKey: ["serverSetupsConfig"],
  });

  // hydrate from server
  useEffect(() => {
    if (status === "success") {
      const cfg: ServerConfig = data?.data?.config || DEFAULT_CONFIG;
      const normalized: ServerConfig = {
        logs_limits: {
          count: cfg?.logs_limits?.count ?? "",
          bytes: cfg?.logs_limits?.bytes ?? "",
        },
        consumption_limits: {
          count: cfg?.consumption_limits?.count ?? "",
          bytes: cfg?.consumption_limits?.bytes ?? "",
        },
        backup_server: {
          ip: cfg?.backup_server?.ip ?? "",
          port: cfg?.backup_server?.port ?? "",
          password: cfg?.backup_server?.password ?? "",
          user: cfg?.backup_server?.user ?? "",
          remote_dir: cfg?.backup_server?.remote_dir ?? "",
        },
        email_from: {
          email: (cfg as any)?.email_from?.email ?? "",
          password: (cfg as any)?.email_from?.password ?? "",
        },
        email_to:
          Array.isArray((cfg as any)?.email_to) && (cfg as any)?.email_to.length
            ? (cfg as any)?.email_to
            : [{ name: "", email: "" }],
        mqtt: {
          ip: cfg?.mqtt?.ip ?? "",
          port: cfg?.mqtt?.port ?? "",
        },
        uploads: {
          max_bytes: (cfg as any)?.uploads?.max_bytes ?? "",
          types: Array.isArray((cfg as any)?.uploads?.types)
            ? (cfg as any)?.uploads?.types
            : [],
        },
      };
      setConfig(normalized);
      setOriginal(normalized);

      const l = splitBytes(normalized.logs_limits.bytes);
      setLogsVal(l.value);
      setLogsUnit(l.unit);
      const c = splitBytes(normalized.consumption_limits.bytes);
      setConsVal(c.value);
      setConsUnit(c.unit);

      // uploads
      const u = splitBytes(normalized.uploads.max_bytes);
      setUploadsVal(u.value);
      setUploadsUnit(u.unit);
    }
  }, [status, data]);

  // SAVE
  const { mutate: saveConfig, isPending: isSaving } = useMutation({
    mutationFn: (payload: ServerConfig) =>
      reqFunction("Config/admin/editServerSetups", { config: payload }),
    onSuccess: (res: any) => {
      if (res?.code === 200) {
        setOriginal(formWithMergedBytes);
        renderToast("تنظیمات با موفقیت ذخیره شد.", "success");
        refetch();
      } else {
        renderToast(res?.farsi_message || "خطا در ذخیره تنظیمات", "err");
      }
    },
    onError: () => renderToast("خطا در ذخیره تنظیمات", "err"),
  });

  // reconstruct bytes for dirty-check + submit
  const formWithMergedBytes: ServerConfig = useMemo(
    () => ({
      ...config,
      logs_limits: {
        ...config.logs_limits,
        bytes: mergeBytes(logsVal, logsUnit),
      },
      consumption_limits: {
        ...config.consumption_limits,
        bytes: mergeBytes(consVal, consUnit),
      },
      // email_to cleanup: trim empties + keep at least one row
      email_to:
        (config.email_to || [])
          .map((r) => ({
            name: r.name?.trim() ?? "",
            email: r.email?.trim() ?? "",
          }))
          .filter((r) => r.name !== "" || r.email !== "").length > 0
          ? (config.email_to || []).map((r) => ({
              name: r.name?.trim() ?? "",
              email: r.email?.trim() ?? "",
            }))
          : [{ name: "", email: "" }],
      // merge uploads size
      uploads: {
        max_bytes: mergeBytes(uploadsVal, uploadsUnit),
        types: (config.uploads?.types || [])
          .map((t) => t.trim())
          .filter(Boolean),
      },
    }),
    [config, logsVal, logsUnit, consVal, consUnit, uploadsVal, uploadsUnit]
  );

  const isDirty =
    JSON.stringify(formWithMergedBytes) !== JSON.stringify(original);

  // validation
  const errors = useMemo(() => {
    const e: Record<string, string> = {};

    if (!positiveInt(config.logs_limits.count))
      e["logs_limits.count"] = "عدد صحیح غیرمنفی";
    if (!positiveInt(config.consumption_limits.count))
      e["consumption_limits.count"] = "عدد صحیح غیرمنفی";

    const lb = mergeBytes(logsVal, logsUnit);
    const cb = mergeBytes(consVal, consUnit);
    const ub = mergeBytes(uploadsVal, uploadsUnit);

    const nonneg = (v: NumOrEmpty) =>
      v === "" || (typeof v === "number" && v >= 0);
    if (!nonneg(lb)) e["logs_limits.bytes"] = "حجم باید غیرمنفی باشد";
    if (!nonneg(cb)) e["consumption_limits.bytes"] = "حجم باید غیرمنفی باشد";
    if (!nonneg(ub)) e["uploads.max_bytes"] = "حجم باید غیرمنفی باشد";

    if (config.backup_server.ip && !isValidIP(config.backup_server.ip))
      e["backup_server.ip"] = "IP نامعتبر";
    if (!portValid(config.backup_server.port))
      e["backup_server.port"] = "پورت بین 1 تا 65535";
    if (!isValidUser(config.backup_server.user))
      e["backup_server.user"] = "نام کاربری نامعتبر";
    if (!isValidRemoteDir(config.backup_server.remote_dir))
      e["backup_server.remote_dir"] = "مسیر باید مطلق باشد (با / شروع شود)";

    if (config.email_from.email && !isValidEmail(config.email_from.email))
      e["email_from.email"] = "ایمیل نامعتبر";

    if (config.mqtt.ip && !isValidIP(config.mqtt.ip))
      e["mqtt.ip"] = "IP نامعتبر";
    if (!portValid(config.mqtt.port)) e["mqtt.port"] = "پورت بین 1 تا 65535";

    // receivers validation
    (config.email_to || []).forEach((r, idx) => {
      const hasAny =
        (r.name?.trim() ?? "") !== "" || (r.email?.trim() ?? "") !== "";
      if (hasAny && r.email && !isValidEmail(r.email)) {
        e[`email_to.${idx}.email`] = "ایمیل نامعتبر";
      }
    });

    // uploads types validation (if provided)
    if ((config.uploads?.types || []).some((t) => !isValidFileTypeToken(t))) {
      e["uploads.types"] =
        "هر مورد باید پسوند مثل .pdf یا MIME مثل image/png باشد";
    }

    return e;
  }, [config, logsUnit, logsVal, consUnit, consVal, uploadsUnit, uploadsVal]);

  const hasErrors = Object.keys(errors).length > 0;

  // receivers handlers
  const addReceiver = () =>
    setConfig((c) => ({
      ...c,
      email_to: [...(c.email_to || []), { name: "", email: "" }],
    }));

  const removeReceiver = (index: number) =>
    setConfig((c) => ({
      ...c,
      email_to: (c.email_to || []).filter((_, i) => i !== index),
    }));

  const updateReceiver = (
    index: number,
    key: keyof EmailToItem,
    value: string
  ) =>
    setConfig((c) => {
      const arr = [...(c.email_to || [])];
      arr[index] = { ...arr[index], [key]: value };
      return { ...c, email_to: arr };
    });

  // uploads: type chips handlers
  const addTypesFromInput = () => {
    const tokens = typeInput
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
    if (!tokens.length) return;
    setConfig((c) => {
      const set = new Set([...(c.uploads?.types || [])]);
      tokens.forEach((t) => {
        if (isValidFileTypeToken(t)) set.add(t);
      });
      return {
        ...c,
        uploads: {
          ...(c.uploads || { max_bytes: "", types: [] }),
          types: Array.from(set),
        },
      };
    });
    setTypeInput("");
  };

  const removeTypeAt = (idx: number) =>
    setConfig((c) => {
      const types = [...(c.uploads?.types || [])];
      types.splice(idx, 1);
      return {
        ...c,
        uploads: { ...(c.uploads || { max_bytes: "", types: [] }), types },
      };
    });

  return (
    <div dir="rtl" className="users-table-container">
      <Card extra="border-none h-max">
        <div className="relative flex items-center justify-between p-4">
          <div className="text-xl font-bold text-navy-700 dark:text-white">
            تنظیمات سرور
          </div>
          <div>
            <CustomButton
              text={isSaving ? "در حال ذخیره..." : "ذخیره"}
              onClick={() => saveConfig(formWithMergedBytes)}
              color="blue"
              disabled={!isDirty || hasErrors || isSaving || isLoading}
            />
          </div>
        </div>

        <Card extra="!h-max p-4">
          {isLoading ? (
            <div className="animate-pulse space-y-3">
              <div className="h-6 w-32 rounded bg-gray-200" />
              <div className="h-24 w-full rounded bg-gray-200" />
            </div>
          ) : (
            <div className="space-y-6">
              {/* Logs Limits */}
              <Section
                title="محدودیت لاگ‌ها"
                subtitle="حداکثر تعداد و حجم ذخیره لاگ‌ها"
              >
                <Field
                  label="تعداد (Count)"
                  error={errors["logs_limits.count"]}
                >
                  <NumberInput
                    value={config.logs_limits.count}
                    onChange={(n) =>
                      setConfig((c) => ({
                        ...c,
                        logs_limits: { ...c.logs_limits, count: n },
                      }))
                    }
                    placeholder="مثلاً 100000"
                  />
                </Field>
                <Field label="حجم" error={errors["logs_limits.bytes"]}>
                  <div className="flex gap-2">
                    <NumberInput
                      value={logsVal}
                      onChange={setLogsVal}
                      placeholder="مثلاً 1024"
                    />
                    <select
                      className="rounded-xl border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-300"
                      value={logsUnit}
                      onChange={(e) => setLogsUnit(e.target.value as UnitKey)}
                    >
                      {unitMap.map((u) => (
                        <option key={u.key} value={u.key}>
                          {u.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </Field>
              </Section>

              {/* Consumption Limits */}
              <Section title="محدودیت مصرف" subtitle="محدودیت تعداد و حجم مصرف">
                <Field
                  label="تعداد (Count)"
                  error={errors["consumption_limits.count"]}
                >
                  <NumberInput
                    value={config.consumption_limits.count}
                    onChange={(n) =>
                      setConfig((c) => ({
                        ...c,
                        consumption_limits: {
                          ...c.consumption_limits,
                          count: n,
                        },
                      }))
                    }
                    placeholder="مثلاً 50000"
                  />
                </Field>
                <Field label="حجم" error={errors["consumption_limits.bytes"]}>
                  <div className="flex gap-2">
                    <NumberInput
                      value={consVal}
                      onChange={setConsVal}
                      placeholder="مثلاً 2048"
                    />
                    <select
                      className="rounded-xl border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-300"
                      value={consUnit}
                      onChange={(e) => setConsUnit(e.target.value as UnitKey)}
                    >
                      {unitMap.map((u) => (
                        <option key={u.key} value={u.key}>
                          {u.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </Field>
              </Section>

              {/* Backup Server */}
              <Section title="سرور پشتیبان" subtitle="مشخصات اتصال">
                <Field label="IP" error={errors["backup_server.ip"]}>
                  <input
                    className="w-full rounded-xl border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-300"
                    value={config.backup_server.ip}
                    placeholder="مثلاً 203.0.113.10"
                    onChange={(e) =>
                      setConfig((c) => ({
                        ...c,
                        backup_server: {
                          ...c.backup_server,
                          ip: e.target.value,
                        },
                      }))
                    }
                  />
                </Field>
                <Field label="پورت" error={errors["backup_server.port"]}>
                  <NumberInput
                    value={config.backup_server.port}
                    onChange={(n) =>
                      setConfig((c) => ({
                        ...c,
                        backup_server: { ...c.backup_server, port: n },
                      }))
                    }
                    placeholder="مثلاً 2222"
                  />
                </Field>

                {/* NEW: Username */}
                <Field label="نام کاربری" error={errors["backup_server.user"]}>
                  <input
                    className="w-full rounded-xl border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-300"
                    value={config.backup_server.user || ""}
                    placeholder="مثلاً root یا deploy"
                    onChange={(e) =>
                      setConfig((c) => ({
                        ...c,
                        backup_server: {
                          ...c.backup_server,
                          user: e.target.value,
                        },
                      }))
                    }
                  />
                </Field>

                <Field label="رمز عبور">
                  <PasswordInput
                    value={config.backup_server.password}
                    onChange={(v: string) =>
                      setConfig((c) => ({
                        ...c,
                        backup_server: { ...c.backup_server, password: v },
                      }))
                    }
                  />
                </Field>

                {/* NEW: Remote directory */}
                <Field
                  label="مسیر پوشه روی سرور پشتیبان"
                  hint="مسیر مطلق؛ فایل‌های CSV اینجا ذخیره می‌شوند"
                  error={errors["backup_server.remote_dir"]}
                >
                  <input
                    className="w-full rounded-xl border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-300"
                    value={config.backup_server.remote_dir || ""}
                    placeholder="/root/systemlog_exports"
                    onChange={(e) =>
                      setConfig((c) => ({
                        ...c,
                        backup_server: {
                          ...c.backup_server,
                          remote_dir: e.target.value,
                        },
                      }))
                    }
                  />
                </Field>
              </Section>

              {/* MQTT */}
              <Section title="MQTT" subtitle="تنظیمات اتصال بروکر">
                <Field label="IP" error={errors["mqtt.ip"]}>
                  <input
                    className="w-full rounded-xl border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-300"
                    value={config.mqtt.ip}
                    placeholder="مثلاً 198.51.100.7"
                    onChange={(e) =>
                      setConfig((c) => ({
                        ...c,
                        mqtt: { ...c.mqtt, ip: e.target.value },
                      }))
                    }
                  />
                </Field>
                <Field label="پورت" error={errors["mqtt.port"]}>
                  <NumberInput
                    value={config.mqtt.port}
                    onChange={(n) =>
                      setConfig((c) => ({ ...c, mqtt: { ...c.mqtt, port: n } }))
                    }
                    placeholder="مثلاً 1883"
                  />
                </Field>
              </Section>

              {/* Sender */}
              <Section
                title="فرستنده ایمیل اضطراری"
                subtitle="اطلاعات ارسال: ایمیل و رمز عبور"
              >
                <Field label="ایمیل" error={errors["email_from.email"]}>
                  <input
                    className="w-full rounded-xl border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-300"
                    value={config.email_from.email}
                    placeholder="name@example.com"
                    onChange={(e) =>
                      setConfig((c) => ({
                        ...c,
                        email_from: { ...c.email_from, email: e.target.value },
                      }))
                    }
                  />
                </Field>
                <Field label="رمز عبور">
                  <PasswordInput
                    value={config.email_from.password}
                    onChange={(v: string) =>
                      setConfig((c) => ({
                        ...c,
                        email_from: { ...c.email_from, password: v },
                      }))
                    }
                  />
                </Field>
              </Section>

              {/* Receivers */}
              <Section
                title="گیرنده‌های ایمیل اضطراری"
                subtitle="لیست نام و ایمیل گیرنده‌ها"
              >
                <div className="col-span-1 md:col-span-2">
                  <div className="space-y-3">
                    {(config.email_to || []).map((r, idx) => (
                      <div
                        key={idx}
                        className="grid grid-cols-1 items-center gap-3 rounded-xl border border-gray-200 p-3 md:grid-cols-5"
                      >
                        <div className="md:col-span-2">
                          <Field label="نام">
                            <input
                              className="w-full rounded-xl border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-300"
                              value={r.name}
                              placeholder="نام گیرنده"
                              onChange={(e) =>
                                updateReceiver(idx, "name", e.target.value)
                              }
                            />
                          </Field>
                        </div>
                        <div className="md:col-span-2">
                          <Field
                            label="ایمیل"
                            error={errors[`email_to.${idx}.email`]}
                          >
                            <input
                              className="w-full rounded-xl border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-300"
                              value={r.email}
                              placeholder="receiver@example.com"
                              onChange={(e) =>
                                updateReceiver(idx, "email", e.target.value)
                              }
                            />
                          </Field>
                        </div>
                        <div className="flex items-end justify-end md:col-span-1">
                          <button
                            type="button"
                            className="inline-flex items-center gap-2 rounded-xl border border-red-200 px-3 py-2 text-red-700 hover:bg-red-50"
                            onClick={() => removeReceiver(idx)}
                            disabled={(config.email_to || []).length <= 1}
                            title={
                              (config.email_to || []).length <= 1
                                ? "حداقل یک گیرنده لازم است"
                                : "حذف"
                            }
                          >
                            <MdDelete />
                            حذف
                          </button>
                        </div>
                      </div>
                    ))}

                    <div className="flex justify-end">
                      <button
                        type="button"
                        className="inline-flex items-center gap-2 rounded-xl border border-gray-300 px-3 py-2 hover:bg-gray-50"
                        onClick={addReceiver}
                      >
                        <MdAdd />
                        افزودن گیرنده
                      </button>
                    </div>
                  </div>
                </div>
              </Section>

              {/* Uploads */}
              <Section
                title="فایل‌های قابل آپلود"
                subtitle="حداکثر حجم و انواع مجاز فایل‌ها"
              >
                <Field
                  label="حداکثر حجم هر فایل"
                  error={errors["uploads.max_bytes"]}
                >
                  <div className="flex gap-2">
                    <NumberInput
                      value={uploadsVal}
                      onChange={setUploadsVal}
                      placeholder="مثلاً 20"
                    />
                    <select
                      className="rounded-xl border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-300"
                      value={uploadsUnit}
                      onChange={(e) =>
                        setUploadsUnit(e.target.value as UnitKey)
                      }
                    >
                      {unitMap.map((u) => (
                        <option key={u.key} value={u.key}>
                          {u.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <span className="mt-1 text-xs text-gray-500">
                    مثال: 20 مگابایت
                  </span>
                </Field>

                <Field
                  label="انواع مجاز فایل"
                  hint="با کاما جدا کنید؛ پسوند مثل .pdf یا MIME مثل image/png"
                  error={errors["uploads.types"]}
                >
                  <div className="flex flex-col gap-2">
                    <div className="flex gap-2">
                      <input
                        className="w-full rounded-xl border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-300"
                        value={typeInput}
                        placeholder=".pdf, .jpg, image/png"
                        onChange={(e) => setTypeInput(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            addTypesFromInput();
                          }
                        }}
                      />
                      <button
                        type="button"
                        className="inline-flex items-center gap-2 rounded-xl border border-gray-300 px-3 py-2 hover:bg-gray-50"
                        onClick={addTypesFromInput}
                        title="افزودن"
                      >
                        <MdAdd />
                        افزودن
                      </button>
                    </div>

                    {/* chips */}
                    <div className="flex flex-wrap gap-2">
                      {(config.uploads?.types || []).length === 0 ? (
                        <span className="text-sm text-gray-400">
                          هنوز نوعی اضافه نشده است
                        </span>
                      ) : (
                        (config.uploads?.types || []).map((t, idx) => (
                          <span
                            key={`${t}-${idx}`}
                            className="inline-flex items-center gap-1 rounded-full border border-gray-200 bg-gray-50 px-3 py-1 text-sm"
                          >
                            {t}
                            <button
                              type="button"
                              className="rounded-full p-1 hover:bg-gray-200"
                              onClick={() => removeTypeAt(idx)}
                              title="حذف"
                            >
                              <MdClose />
                            </button>
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                </Field>
              </Section>
            </div>
          )}
        </Card>
      </Card>
    </div>
  );
};
